/**
 * F42 — admin venue management API client (B1 surface). WAVE-2 (B1).
 *
 * The list/detail RICHNESS extends F10's existing GET /admin/venues +
 * /admin/venues/:id (A1 backend); the action calls hit the F42 ops routes.
 * Component tests drive `adminVenues.fixture.ts` (swapped via the vitest alias);
 * production code uses these real `adminFetch` calls.
 *
 * Permission gating is server-side; <ProtectedRoute> gates navigation.
 */
import type {
  AddVenueNoteInput,
  AdminEditVenueFieldsInput,
  AdminVenueCancellationPolicyInput,
  AdminVenueDetail,
  AdminVenueListMeta,
  AdminVenueListRow,
  AdminVenueVerificationFilter,
  FlagVenueInput,
  HostedTournamentRow,
  LockFieldsInput,
  NameLockOverride,
  ReassignVenueOwnershipInput,
  ResolveFlagInput,
  RestoreVenueInput,
  SuspendVenueInput,
  UnlockFieldsInput,
  VenueAdminAuditEntry,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

/** PATCH /admin/venues/:id/claim-status body (claim override; owner optional). */
export interface SetClaimStatusInput {
  readonly claimStatus: string;
  /** UUID — set when promoting to `claimed`; `null` clears the owner. */
  readonly ownerB2bAccountId?: string | null;
}

/**
 * `PATCH /admin/venues/:id/fields` body: the frozen F42 edit input plus the
 * F09 v2 name-lock override (§11 point 6). A `name` change on a verified venue
 * returns 422 `NAME_LOCKED_VERIFIED` unless `nameLockOverride` is sent; the
 * override needs `verifications:review` on top of `venues:admin_edit`.
 * `verificationStatus` is no longer an editable field (422 `UNKNOWN_FIELD`).
 */
export type AdminEditVenueFieldsRequest = AdminEditVenueFieldsInput & {
  readonly nameLockOverride?: NameLockOverride;
};

export interface AdminVenueListResponse {
  readonly data: readonly AdminVenueListRow[];
  readonly meta: AdminVenueListMeta;
}

export interface AdminVenueListQuery {
  readonly q?: string;
  readonly status?: string;
  readonly tier?: string;
  readonly claimStatus?: string;
  readonly sport?: string;
  readonly cazaCode?: string;
  /** F09 v2 (E41) — `verified | unverified | open_request`, sent as `?verification=`. */
  readonly verification?: AdminVenueVerificationFilter;
  readonly flagged?: boolean;
  readonly cursor?: string;
  readonly limit?: number;
  /** 1-based page index (page-numbered pagination). */
  readonly page?: number;
  /** Page size (default 25 server-side, max 100). */
  readonly perPage?: number;
}

export interface VenueAuditResponse {
  readonly data: readonly VenueAdminAuditEntry[];
  readonly nextCursor: string | null;
}

/** A1's GET /admin/venues/:id returns the detail under a `data` envelope. */
interface DetailEnvelope {
  readonly data: AdminVenueDetail;
}

export const adminVenuesApi = {
  async list(query: AdminVenueListQuery): Promise<AdminVenueListResponse> {
    const qs = buildQuery({
      q: query.q,
      status: query.status,
      tier: query.tier,
      claimStatus: query.claimStatus,
      sport: query.sport,
      cazaCode: query.cazaCode,
      verification: query.verification,
      flagged: query.flagged,
      cursor: query.cursor,
      limit: query.limit,
      page: query.page,
      perPage: query.perPage,
    });
    return adminFetch<AdminVenueListResponse>(`/admin/venues${qs}`);
  },

  async getDetail(venueId: string): Promise<AdminVenueDetail> {
    const res = await adminFetch<DetailEnvelope>(`/admin/venues/${encodeURIComponent(venueId)}`);
    return res.data;
  },

  async editFields(
    venueId: string,
    input: AdminEditVenueFieldsRequest,
    ifMatch: string,
  ): Promise<AdminVenueDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/venues/${encodeURIComponent(venueId)}/fields`,
      { method: 'PATCH', body: input, headers: { 'if-match': ifMatch } },
    );
    return res.data;
  },

  async setCancellationPolicy(
    venueId: string,
    input: AdminVenueCancellationPolicyInput,
    ifMatch: string,
  ): Promise<AdminVenueDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/venues/${encodeURIComponent(venueId)}/cancellation-policy`,
      { method: 'PATCH', body: input, headers: { 'if-match': ifMatch } },
    );
    return res.data;
  },

  async lockFields(venueId: string, input: LockFieldsInput): Promise<AdminVenueDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/venues/${encodeURIComponent(venueId)}/lock-fields`,
      { method: 'POST', body: input },
    );
    return res.data;
  },

  async unlockFields(venueId: string, input: UnlockFieldsInput): Promise<AdminVenueDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/venues/${encodeURIComponent(venueId)}/unlock-fields`,
      { method: 'POST', body: input },
    );
    return res.data;
  },

  async suspend(venueId: string, input: SuspendVenueInput): Promise<AdminVenueDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/venues/${encodeURIComponent(venueId)}/suspend`,
      { method: 'POST', body: input },
    );
    return res.data;
  },

  async restore(venueId: string, input: RestoreVenueInput): Promise<AdminVenueDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/venues/${encodeURIComponent(venueId)}/restore`,
      { method: 'POST', body: input },
    );
    return res.data;
  },

  async flag(venueId: string, input: FlagVenueInput): Promise<AdminVenueDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/venues/${encodeURIComponent(venueId)}/flag`,
      { method: 'POST', body: input },
    );
    return res.data;
  },

  async resolveFlag(venueId: string, input: ResolveFlagInput): Promise<AdminVenueDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/venues/${encodeURIComponent(venueId)}/resolve-flag`,
      { method: 'POST', body: input },
    );
    return res.data;
  },

  async addNote(venueId: string, input: AddVenueNoteInput): Promise<void> {
    await adminFetch<void>(`/admin/venues/${encodeURIComponent(venueId)}/notes`, {
      method: 'POST',
      body: input,
    });
  },

  async getAudit(venueId: string, cursor?: string): Promise<VenueAuditResponse> {
    const qs = buildQuery({ cursor });
    return adminFetch<VenueAuditResponse>(
      `/admin/venues/${encodeURIComponent(venueId)}/audit${qs}`,
    );
  },

  async getHostedTournaments(venueId: string): Promise<readonly HostedTournamentRow[]> {
    const res = await adminFetch<{ data: readonly HostedTournamentRow[] }>(
      `/admin/venues/${encodeURIComponent(venueId)}/hosted-tournaments`,
    );
    return res.data;
  },

  /**
   * Admin Super-Powers — PATCH /admin/venues/:id/owner (`venues:write`).
   * Force-assign / reassign / clear (`newOwnerB2bAccountId: null`) a venue's
   * owning B2B account. 422 ACCOUNT_NOT_FOUND / ACCOUNT_NOT_ACTIVE (when an
   * account id is supplied); 404 VENUE_NOT_FOUND. Returns the venue; the page
   * refetches the rich detail rather than coupling to the F10 `Venue` shape.
   */
  async reassignOwner(venueId: string, input: ReassignVenueOwnershipInput): Promise<unknown> {
    return adminFetch<unknown>(`/admin/venues/${encodeURIComponent(venueId)}/owner`, {
      method: 'PATCH',
      body: input as unknown as Record<string, unknown>,
    });
  },

  /**
   * Admin Super-Powers — PATCH /admin/venues/:id/claim-status (`venues:write`).
   * Manual claim-state override. Same ACCOUNT_NOT_FOUND / ACCOUNT_NOT_ACTIVE
   * 422s as `reassignOwner` when `ownerB2bAccountId` is supplied. Returns
   * `{ data: { ok: true } }`.
   */
  async setClaimStatus(venueId: string, input: SetClaimStatusInput): Promise<unknown> {
    return adminFetch<unknown>(`/admin/venues/${encodeURIComponent(venueId)}/claim-status`, {
      method: 'PATCH',
      body: {
        claimStatus: input.claimStatus,
        ...(input.ownerB2bAccountId !== undefined
          ? { ownerB2bAccountId: input.ownerB2bAccountId }
          : {}),
      },
    });
  },
};
